/**
 * NodePalette — the left rail of draggable/clickable node types.
 *
 * Lists every supported node type (from `NODE_TYPE_LIST`, keyed to the backend
 * constants). Clicking a palette entry adds a node to the canvas via the store;
 * entries are also draggable so the editor can drop them at the cursor. The
 * palette never hardcodes node-type strings — it iterates `NODE_TYPE_META`.
 */

import type { ReactNode } from 'react';
import {
  NODE_TYPE_LIST,
  NODE_TYPE_META,
  type NodeType,
} from '../../lib/agents';

interface NodePaletteProps {
  /** Add a node of the given type (the editor positions it). */
  onAdd: (type: NodeType) => void;
}

export function NodePalette({ onAdd }: NodePaletteProps): ReactNode {
  return (
    <aside
      className="w-60 shrink-0 border-r border-steel-200 bg-steel-50 overflow-y-auto"
      data-testid="node-palette"
    >
      <div className="px-4 py-3 border-b border-steel-200">
        <h3 className="text-sm font-semibold text-navy-900">Node Palette</h3>
        <p className="mt-0.5 text-[11px] text-steel-500">
          Click or drag a node onto the canvas.
        </p>
      </div>
      <ul className="p-3 space-y-2">
        {NODE_TYPE_LIST.map((type) => {
          const meta = NODE_TYPE_META[type];
          return (
            <li key={type}>
              <button
                type="button"
                data-testid={`palette-${type}`}
                onClick={() => onAdd(type)}
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/agent-node-type', type);
                  event.dataTransfer.effectAllowed = 'move';
                }}
                className={`w-full text-left rounded-lg border-2 px-3 py-2 transition-shadow hover:shadow-md ${meta.accent}`}
              >
                <span className="block text-sm font-medium text-navy-900">
                  {meta.label}
                </span>
                <span className="block text-[11px] text-steel-500">
                  {meta.description}
                </span>
                <span className="mt-1 inline-block text-[10px] font-mono text-steel-400">
                  {type}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export default NodePalette;
